import React, { useState } from 'react';
import IndicatorParametersForm from './IndicatorParametersForm';
import { INDICATOR_DEFAULTS } from '../../utils/indicatorDefaults';
import { getPresetsForIndicator, applyPreset } from '../../utils/indicatorPresets';
import { useCustomIndicators } from '../../contexts/CustomIndicatorContext';
import { X } from 'lucide-react';

/**
 * IndicatorSettingsModal Component
 *
 * Full-screen modal for customizing indicator parameters and colors
 *
 * @param {boolean} isOpen - Whether modal is open
 * @param {function} onClose - Callback to close modal
 * @param {function} onSave - Callback when settings are saved
 * @param {string} indicator - Indicator name (e.g., 'macd', 'rsi')
 * @param {object} currentSettings - Current indicator settings
 */
function IndicatorSettingsModal({
  isOpen,
  onClose,
  onSave,
  indicator,
  currentSettings = {}
}) {
  // Merge current settings with defaults
  const defaultSettings = INDICATOR_DEFAULTS[indicator] || {};
  const initialSettings = {
    ...defaultSettings,
    ...currentSettings,
    colors: {
      ...defaultSettings.colors,
      ...currentSettings.colors
    }
  };

  const [settings, setSettings] = useState(initialSettings);
  const [activeTab, setActiveTab] = useState('parameters');
  const [isSaving, setIsSaving] = useState(false);

  // Custom indicator state
  const [customIndicatorName, setCustomIndicatorName] = useState('');
  const [isSavingCustom, setIsSavingCustom] = useState(false);
  const { addCustomIndicator } = useCustomIndicators();

  // Get indicator display name
  const getIndicatorName = () => {
    const names = {
      macd: 'MACD',
      rsi: 'RSI',
      bollinger: 'Bollinger Bands',
      volume: 'Volume',
      sma_50: 'SMA 50',
      sma_200: 'SMA 200',
      sma_720: 'SMA 720',
      ema: 'EMA',
      vwap: 'VWAP',
      ac: 'Acceleration/Deceleration'
    };
    return names[indicator] || indicator.toUpperCase();
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(indicator, settings);
      onClose();
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    if (window.confirm('Reset all settings to defaults?')) {
      setSettings(defaultSettings);
    }
  };

  const handleSaveAsCustom = async () => {
    if (!customIndicatorName.trim()) {
      alert('Please enter a name for your custom indicator');
      return;
    }

    setIsSavingCustom(true);
    try {
      await addCustomIndicator({
        name: customIndicatorName,
        base_indicator: indicator,
        settings: settings
      });

      alert(`✓ Custom indicator "${customIndicatorName}" saved successfully!`);
      setCustomIndicatorName(''); // Clear input
    } catch (error) {
      console.error('Error saving custom indicator:', error);
      const errorMsg = error.response?.data?.name?.[0] || 'Failed to save custom indicator';
      alert(errorMsg);
    } finally {
      setIsSavingCustom(false);
    }
  };

  const handlePresetClick = (presetKey) => {
    const presets = getPresetsForIndicator(indicator);
    const preset = presets[presetKey];
    if (preset) {
      const newSettings = applyPreset(settings, preset.settings);
      setSettings(newSettings);
    }
  };

  if (!isOpen) return null;

  const presets = getPresetsForIndicator(indicator);
  const hasPresets = Object.keys(presets).length > 0;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-background border border-muted rounded-lg w-full max-w-2xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 pb-4 border-b border-muted">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-primary">
              {getIndicatorName()} Settings
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Save as Custom Indicator Section */}
        <div className="px-6 py-4 border-b border-muted bg-gray-800/30">
          <h3 className="text-sm font-medium text-text mb-2">
            Save as Custom Indicator
          </h3>
          <p className="text-xs text-gray-400 mb-3">
            Save these settings to reuse across charts
          </p>
          <div className="flex gap-2">
            <input
              type="text"
              value={customIndicatorName}
              onChange={(e) => setCustomIndicatorName(e.target.value)}
              placeholder={`My Custom ${getIndicatorName()}`}
              className="flex-1 px-3 py-2 bg-background border border-muted rounded text-text text-sm focus:outline-none focus:border-primary"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSaveAsCustom();
                }
              }}
            />
            <button
              onClick={handleSaveAsCustom}
              disabled={isSavingCustom || !customIndicatorName.trim()}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded text-sm transition"
            >
              {isSavingCustom ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-muted">
          <button
            onClick={() => setActiveTab('parameters')}
            className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
              activeTab === 'parameters'
                ? 'text-primary border-b-2 border-primary'
                : 'text-gray-400 hover:text-text'
            }`}
          >
            Parameters
          </button>
          <button
            onClick={() => setActiveTab('colors')}
            className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
              activeTab === 'colors'
                ? 'text-primary border-b-2 border-primary'
                : 'text-gray-400 hover:text-text'
            }`}
          >
            Colors
          </button>
          {hasPresets && (
            <button
              onClick={() => setActiveTab('presets')}
              className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
                activeTab === 'presets'
                  ? 'text-primary border-b-2 border-primary'
                  : 'text-gray-400 hover:text-text'
              }`}
            >
              Presets
            </button>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'presets' && hasPresets ? (
            <div className="space-y-3">
              <p className="text-sm text-gray-400 mb-4">
                Choose a preset configuration to quickly apply common settings
              </p>
              {Object.entries(presets).map(([key, preset]) => (
                <button
                  key={key}
                  onClick={() => handlePresetClick(key)}
                  className="w-full text-left px-4 py-3 bg-muted hover:bg-muted/80 rounded transition-colors"
                >
                  <div className="font-medium text-text">{preset.name}</div>
                  <div className="text-sm text-gray-400 mt-1">
                    {preset.description}
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <IndicatorParametersForm
              indicator={indicator}
              settings={settings}
              onSettingsChange={setSettings}
              activeTab={activeTab}
            />
          )}
        </div>

        {/* Footer */}
        <div className="p-6 pt-4 border-t border-muted">
          <div className="flex gap-3">
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex-1 bg-primary text-white px-4 py-2 rounded hover:bg-primary/80 transition disabled:opacity-50"
            >
              {isSaving ? 'Applying...' : 'Apply to Widget'}
            </button>
            <button
              onClick={handleReset}
              className="px-4 py-2 bg-gray-700 text-text rounded hover:bg-gray-600 transition"
            >
              Reset
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-muted text-text rounded hover:bg-muted/80 transition"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default IndicatorSettingsModal;
